import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingBreakdownCard = ({ pricing, payment, onDownloadReceipt }) => {
  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-3 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="DollarSign" size={20} color="var(--color-primary)" strokeWidth={2} />
        </div>
        <h2 className="font-heading text-2xl font-semibold text-foreground">
          Pricing Breakdown
        </h2>
      </div>

      <div className="space-y-4">
        {/* Rental Charges */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="font-body text-base text-muted-foreground">
              ${pricing?.dailyRate} × {pricing?.days} days
            </span>
            <span className="font-body text-base font-medium text-foreground">
              ${pricing?.subtotal?.toFixed(2)}
            </span>
          </div>

          {pricing?.weeklyDiscount > 0 && (
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <Icon name="Tag" size={16} color="var(--color-success)" strokeWidth={2} />
                <span className="font-body text-base text-success">
                  Weekly discount ({pricing?.discountPercent}%)
                </span>
              </div>
              <span className="font-body text-base font-medium text-success">
                -${pricing?.weeklyDiscount?.toFixed(2)}
              </span>
            </div>
          )}
        </div>

        <div className="border-t border-border pt-3 space-y-3">
          <div className="flex justify-between items-center">
            <span className="font-body text-base text-muted-foreground">
              Security Deposit
            </span>
            <span className="font-body text-base font-medium text-foreground">
              ${pricing?.securityDeposit?.toFixed(2)}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="font-body text-base text-muted-foreground">
              Cleaning Fee
            </span>
            <span className="font-body text-base font-medium text-foreground">
              ${pricing?.cleaningFee?.toFixed(2)}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="font-body text-base text-muted-foreground">
              Tax ({pricing?.taxRate}%)
            </span>
            <span className="font-body text-base font-medium text-foreground">
              ${pricing?.tax?.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="border-t-2 border-border pt-4">
          <div className="flex justify-between items-center">
            <span className="font-heading text-xl font-semibold text-foreground">
              Total Paid
            </span>
            <span className="font-heading text-2xl font-bold text-primary">
              ${pricing?.total?.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Payment Confirmation */}
        <div className="border-t border-border pt-4 space-y-3">
          <h3 className="font-heading text-base font-semibold text-foreground">
            Payment Confirmation
          </h3>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-body text-sm text-muted-foreground">Payment Method</span>
              <div className="flex items-center space-x-2">
                <Icon name="CreditCard" size={14} strokeWidth={2} />
                <span className="font-body text-sm font-medium text-foreground">
                  {payment?.method} ••••{payment?.last4}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="font-body text-sm text-muted-foreground">Transaction ID</span>
              <span className="font-body text-xs font-mono text-foreground">
                {payment?.transactionId}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="font-body text-sm text-muted-foreground">Payment Date</span>
              <span className="font-body text-sm font-medium text-foreground">
                {formatDate(payment?.paidAt)}
              </span>
            </div>
          </div>
        </div>

        {/* Refund Policy */}
        <div className="border-t border-border pt-4">
          <div className="flex items-start space-x-3 p-3 bg-muted rounded-md">
            <Icon name="Info" size={18} color="var(--color-primary)" strokeWidth={2} />
            <div>
              <p className="font-body text-sm font-medium text-foreground mb-1">
                Refund Policy
              </p>
              <p className="font-body text-xs text-muted-foreground">
                Security deposit will be refunded within 3-5 business days after trailer return and inspection. Free cancellation up to 48 hours before pickup.
              </p>
            </div>
          </div>
        </div>

        {/* Download Receipt Button */}
        <Button
          variant="outline"
          size="default"
          fullWidth
          iconName="Download"
          iconPosition="left"
          onClick={onDownloadReceipt}
        >
          Download Receipt
        </Button>
      </div>
    </div>
  );
};

export default PricingBreakdownCard;